import { FC, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import {
  Box, Grid, Table, TableBody, TableHead, TableRow, TableCell, tableCellClasses, Chip, Button, CircularProgress,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUnits } from "../../store/unitsStore/unitsThunk";
import { useInfiniteReport } from "../../hooks/useInfiniteReport";
import ReusableReportHeader from "../Reports/ReusableReportHeader";
import LocationReportFilter from "./pickers/LocationReportFilter";
import { useReportLocationId } from "./pickers/useReportLocationId";
import { FilterSelect } from "./InvFilters";
import Filters from "../Filters/Filters";

// GRN report — one row per line item (header rowSpans its SKU lines), server-paginated + server-side filters.

const STATUS_COLOR: Record<string, string> = { draft: "#b08900", posted: "#49a563", cancelled: "#c33b3b" };
const GRN_STATUSES = ["draft", "posted", "cancelled"];
const statusLabel = (s: string) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#eaecf0", color: "#000", border: "0.75px solid #d9d4d4",
    padding: 5, fontSize: "9pt", paddingLeft: "12px", fontWeight: 600, whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "9pt", fontWeight: "normal", color: "#292d35",
    padding: 5, border: "0.75px solid #d9d4d4", paddingLeft: "12px", verticalAlign: "top",
  },
}));

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");
const num = (n: any) => Number(n ?? 0);
const money = (n: any) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const esc = (s: any) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

// Header (GRN-level, spanned) + item-level columns — the whole document.
const HEADER_COLS = ["Date", "GRN #", "Location", "Supplier", "PO Ref", "Status", "Posted", "Created By", "Received By"];
const ITEM_COLS = ["Item" /* item code shown under the name */, "Batch", "Mfg", "Expiry", "Qty", "Unit", "Purchase", "Sale", "MRP", "Barcode"];
const ALL_COLS = [...HEADER_COLS, ...ITEM_COLS];

interface GRNReportProps {
  // Item view page embeds this report scoped to one SKU: the page header is
  // hidden and every fetch carries skuId so only that item's GRNs come back.
  skuId?: string;
  embedded?: boolean;
}

const GRNReport: FC<GRNReportProps> = ({ skuId, embedded }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const unitsById = useAppSelector((state) => state.units.byId);
  const unitsList = useAppSelector((state) => state.units.units);

  // Item `unit` is stored as a MeasuringUnit id — resolve to a readable code/name.
  const unitText = (u?: string) => (u ? unitsById?.[u]?.code || unitsById?.[u]?.name || u : "-");

  const [locationId, setLocationId] = useReportLocationId({ startOn: "all" }); // opens on All Locations
  const [statusFilter, setStatusFilter] = useState("");
  const [postedFrom, setPostedFrom] = useState("");
  const [postedTo, setPostedTo] = useState("");
  // Bumped on reset to remount the shared date-range dropdown back to "Select Date".
  const [filtersKey, setFiltersKey] = useState(0);

  useEffect(() => {
    if (!unitsList || unitsList.length === 0) dispatch(fetchUnits());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side, scroll-paginated data (50 records/page).
  const { rows, total, loading, loadingMore, hasMore, loadMore, collectAll } = useInfiniteReport(
    "/api/v1/erp/grns",
    { locationId, status: statusFilter, postedFrom, postedTo, skuId }
  );

  // Shared Filters dropdown emits "start=YYYY-MM-DD&end=YYYY-MM-DD" (empty for "Select Date").
  const handleDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    unstable_batchedUpdates(() => {
      setPostedFrom(params.get("start") || "");
      setPostedTo(params.get("end") || "");
    });
  };

  const resetFilters = () => {
    setLocationId(""); setStatusFilter(""); setPostedFrom(""); setPostedTo("");
    setFiltersKey((k) => k + 1);
  };

  const onScroll = (e: any) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 300 && hasMore && !loadingMore && !loading) loadMore();
  };

  const rangeText = postedFrom || postedTo ? `Posted ${postedFrom ? fmtDate(postedFrom) : "…"} – ${postedTo ? fmtDate(postedTo) : "…"}` : "All dates";

  // -------- export (pages through the FULL filtered set, not just what's loaded) --------
  const flatRows = (data: any[]): string[][] =>
    data.flatMap((g: any) => {
      const items = (g.items && g.items.length) ? g.items : [null];
      return items.map((it: any): string[] => [
        fmtDate(g.createdAt), g.grnNumber || "", g.locationId?.name || "", g.supplierId?.name || g.supplierId?.legalName || "",
        g.refVoucherId?.voucherNumber || "", statusLabel(g.status || ""), fmtDate(g.postedAt), g.createdBy?.name || "", g.receivedBy?.name || "",
        it?.skuId ? `${it.skuId.name || ""}${it.skuId.itemCode ? ` (${it.skuId.itemCode})` : ""}` : "", it?.batchNumber || "",
        it?.mfgDate ? fmtDate(it.mfgDate) : "", it?.expiryDate ? fmtDate(it.expiryDate) : "",
        it ? String(num(it.qtyReceived)) : "", it ? unitText(it.unit) : "",
        it ? String(num(it.purchasePrice)) : "", it ? String(num(it.salePrice)) : "", it ? String(num(it.mrp)) : "",
        it?.barcode || "",
      ]);
    });

  const buildTableHtml = (data: any[]) => `
    <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;font-family:Arial;font-size:11px">
      <thead><tr style="background:#eaecf0">${ALL_COLS.map((c) => `<th align="left">${esc(c)}</th>`).join("")}</tr></thead>
      <tbody>${flatRows(data).map((r) => `<tr>${r.map((v) => `<td>${esc(v)}</td>`).join("")}</tr>`).join("")}</tbody>
    </table>`;

  const handleDownloadExcel = async () => {
    const all = await collectAll();
    const html = `<html><head><meta charset="utf-8" /></head><body>${buildTableHtml(all)}</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `GRN_Report_${new Date().toISOString().split("T")[0]}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    const all = await collectAll();
    const lines = all.reduce((s: number, g: any) => s + Math.max((g.items || []).length, 1), 0);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>GRN Report</title>
      <style>@page{size:A4 landscape;margin:10mm}
      body{font-family:Arial;padding:0} h3{margin:0 0 4px} .sub{color:#666;font-size:12px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%;font-size:10px} th,td{border:0.75px solid #d9d4d4;padding:4px;text-align:left}
      thead tr{background:#eaecf0}</style></head>
      <body><h3>Goods Receipt (GRN) Report</h3><div class="sub">${esc(rangeText)} · ${all.length} GRNs · ${lines} lines</div>
      ${buildTableHtml(all)}</body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:absolute;width:0;height:0;border:0;left:-9999px;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return false;
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus(); iframe.contentWindow?.print();
        setTimeout(() => document.body.removeChild(iframe), 500);
      }, 300);
    };
    return "print";
  };

  // Dashboard report-card's download button opens this page with ?download=excel|pdf —
  // trigger that export once and clean the URL.
  useEffect(() => {
    const dl = new URLSearchParams(window.location.search).get("download");
    if (!dl) return;
    window.history.replaceState({}, "", window.location.pathname);
    const t = setTimeout(() => { dl === "pdf" ? handleDownloadPDF() : handleDownloadExcel(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: "100%", pr: 1 }}>
      <Box sx={{ width: "100%", overflow: "hidden", mb: 2 }} className={"c-card2"}>
        {!embedded && (
          <ReusableReportHeader
            reportType="grn"
            title="Goods Receipt (GRN) Report"
            subtitle={`${rangeText} · ${total} GRNs`}
            recordCount={total}
            isLoading={loading}
            backTo="/inventory/grn"
            showPrint={false}
            showShare={false}
            onDownloadPDF={handleDownloadPDF}
            onDownloadExcel={handleDownloadExcel}
          />
        )}

        {/* Filters strip */}
        <Grid container spacing={1.5} alignItems="center" p={2} style={{ borderBottom: ".5px solid #9da7b7" }}>
          <Grid item>
            <LocationReportFilter borderless width={200} value={locationId} onChange={(v: string) => setLocationId(v)} label="Location" activeOnly={false} />
          </Grid>
          <Grid item>
            <FilterSelect
              value={statusFilter}
              onChange={(v: string) => setStatusFilter(v)}
              placeholder="Status"
              width={150}
              options={GRN_STATUSES.map((s) => ({ label: statusLabel(s), value: s }))}
            />
          </Grid>
          <Grid item>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Posted on</span>
              <Filters key={filtersKey} type="inventory-grn" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleDateFilter} />
            </div>
          </Grid>
          <Grid item>
            <Button size="small" color="inherit" startIcon={<RestartAltIcon />} onClick={resetFilters} sx={{ color: "#7a8290" }}>
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Report table — one GRN header spans its line items; scroll loads more */}
        <div onScroll={onScroll} style={{ maxHeight: "62vh", overflow: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 1800 }} size="small" aria-label="grn report">
            <TableHead>
              <TableRow>
                {ALL_COLS.map((c) => <StyledTableCell key={c}>{c}</StyledTableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? rows.map((g: any) => {
                const items = (g.items && g.items.length) ? g.items : [null];
                const span = items.length;
                return items.map((it: any, idx: number) => (
                  <TableRow key={`${g._id}-${idx}`}>
                    {idx === 0 && (
                      <>
                        <StyledTableCell rowSpan={span}>{fmtDate(g.createdAt)}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>
                          <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => navigate(`/inventory/grn/${g._id}`)}>
                            {g.grnNumber}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell rowSpan={span}>{g.locationId?.name || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>{g.supplierId?.name || g.supplierId?.legalName || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>{g.refVoucherId?.voucherNumber || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>
                          <Chip size="small" label={statusLabel(g.status || "")} sx={{ color: STATUS_COLOR[g.status] || "#555" }} />
                        </StyledTableCell>
                        <StyledTableCell rowSpan={span}>{fmtDate(g.postedAt)}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>{g.createdBy?.name || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>{g.receivedBy?.name || "-"}</StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>
                      {it?.skuId?.name || "-"}
                      {it?.skuId?.itemCode ? (
                        <div style={{ color: "#9da7b7", fontSize: "8pt", marginTop: 2 }}>{it.skuId.itemCode}</div>
                      ) : null}
                    </StyledTableCell>
                    <StyledTableCell>{it?.batchNumber || "-"}</StyledTableCell>
                    <StyledTableCell>{it?.mfgDate ? fmtDate(it.mfgDate) : "-"}</StyledTableCell>
                    <StyledTableCell>{it?.expiryDate ? fmtDate(it.expiryDate) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? num(it.qtyReceived) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? unitText(it.unit) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? money(it.purchasePrice) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? money(it.salePrice) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? money(it.mrp) : "-"}</StyledTableCell>
                    <StyledTableCell>{it?.barcode || "-"}</StyledTableCell>
                  </TableRow>
                ));
              }) : (
                <TableRow>
                  <StyledTableCell colSpan={ALL_COLS.length} style={{ textAlign: "center", color: "#9da7b7", padding: 24 }}>
                    {loading ? "Loading…" : "No goods receipts match the selected filters."}
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {loadingMore ? (
            <div style={{ textAlign: "center", padding: 12 }}><CircularProgress size={20} /></div>
          ) : (!hasMore && rows.length > 0) ? (
            <div style={{ textAlign: "center", padding: 10, color: "#c7ccd4", fontSize: 12 }}>All {total} GRNs loaded</div>
          ) : null}
        </div>
      </Box>
    </Box>
  );
};

export default GRNReport;
